import { useMemo, useState } from "react"
import { useFeatures } from "@/config/context"
import type { NostrEvent } from "@/nostr/types"
import { usePublish } from "@/hooks/usePublish"
import { useRelays } from "@/state/RelayContext"
import { useAuth } from "@/state/AuthContext"
import { simplifyUrl } from "@/utils/url"
import { Icon } from "./Icon"
import { LoginDialog } from "./LoginDialog"

export interface ComposerProps {
  replyTo?: NostrEvent
  root?: NostrEvent
  placeholder?: string
  autoFocus?: boolean
  onPublished?: () => void
  compact?: boolean
}

const MAX_LENGTH = 8000

/** Note composer with an explicit relay picker (Jumble's "post settings"). */
export function Composer({ replyTo, root, placeholder, autoFocus, onPublished, compact }: ComposerProps) {
  const features = useFeatures()
  const { canSign, pubkey } = useAuth()
  const { writeTargets } = useRelays()
  const { publishNote } = usePublish()
  const [content, setContent] = useState("")
  const [selected, setSelected] = useState<string[] | null>(null)
  const [showRelays, setShowRelays] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [publishing, setPublishing] = useState(false)

  const relays = useMemo(
    () => (selected ? selected : writeTargets).filter((url) => writeTargets.includes(url)),
    [selected, writeTargets],
  )

  if (!features.composer) return null

  if (!canSign || !pubkey) {
    return (
      <div className={`composer composer-locked ${compact ? "composer-compact" : ""}`}>
        <p>Log in with a signer to publish notes, replies and reactions.</p>
        <button type="button" className="btn btn-primary" onClick={() => setShowLogin(true)}>
          Log in
        </button>
        <LoginDialog open={showLogin} onClose={() => setShowLogin(false)} />
      </div>
    )
  }

  const onPublish = async () => {
    if (!content.trim() || publishing) return
    setPublishing(true)
    const outcome = await publishNote(content, { replyTo, root, relays })
    setPublishing(false)
    if (outcome.ok) {
      setContent("")
      onPublished?.()
    }
  }

  return (
    <div className={`composer ${compact ? "composer-compact" : ""}`}>
      <label className="visually-hidden" htmlFor="composer-input">
        {replyTo ? "Write a reply" : "Write a note"}
      </label>
      <textarea
        id="composer-input"
        className="composer-input"
        value={content}
        onChange={(event) => setContent(event.target.value.slice(0, MAX_LENGTH))}
        placeholder={placeholder ?? (replyTo ? "Write your reply…" : "What is happening on your relays?")}
        rows={compact ? 2 : 3}
        autoFocus={autoFocus}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void onPublish()
        }}
      />
      <div className="composer-footer">
        <div className="composer-relays">
          <button
            type="button"
            className="chip"
            onClick={() => setShowRelays((value) => !value)}
            aria-expanded={showRelays}
          >
            <Icon name="relay" size={14} />
            {relays.length === writeTargets.length
              ? `All ${writeTargets.length} relays`
              : `${relays.length} of ${writeTargets.length} relays`}
            <Icon name="chevron-down" size={12} />
          </button>
          {showRelays && (
            <div className="relay-picker" role="group" aria-label="Publish to relays">
              {writeTargets.length === 0 && <p className="muted">Add a relay first.</p>}
              {writeTargets.map((url) => {
                const checked = relays.includes(url)
                return (
                  <label key={url} className="relay-picker-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const base = relays.includes(url)
                          ? relays.filter((item) => item !== url)
                          : [...relays, url]
                        setSelected(base)
                      }}
                    />
                    <span>{simplifyUrl(url)}</span>
                  </label>
                )
              })}
            </div>
          )}
        </div>
        <div className="composer-actions">
          <span className="composer-count" aria-live="polite">
            {content.length}/{MAX_LENGTH}
          </span>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onPublish}
            disabled={!content.trim() || publishing}
          >
            {publishing ? "Publishing…" : replyTo ? "Reply" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  )
}
